import { useMemo } from 'react'
import { Avatar } from '@/components/Avatar'
import { FormGroup } from '@/components/Form'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Member } from '@/features/iam/members/Member'
import { TeamSelection } from '@/features/jobs/drafting/DraftPersister'
import { observer } from 'mobx-react-lite'
import { type TeamSelectorViewModel } from '@/features/jobs/drafting/TeamSelectorViewModel'

const myselfDefaultPlaceholder = 'Myself'

export interface CompactTeamSelectorContainerProps {
  vm: TeamSelectorViewModel
}

export const CompactTeamSelectorContainer = observer(
  function CompactTeamSelectorContainer({
    vm,
  }: CompactTeamSelectorContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.availableTeams, (item, text) => {
          const sourceText =
            item === TeamSelection.myself ? 'Myself' : item.name
          return sourceText.toLowerCase().includes(text.toLowerCase())
        }),
      [vm.availableTeams],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    const nullSelectionPlaceholder = vm.nullSelectionPlaceholder

    return (
      <FormGroup>
        <CustomSelect
          {...selectProps}
          placeholder="Select a Team"
          itemToString={(v) =>
            v === TeamSelection.myself
              ? nullSelectionPlaceholder
              : String(v?.name)
          }
          isDisabled={vm.disabled}
          focus$={vm.step.becomeActive$}
          onFocus={vm.step.onBecameActive}
          value={vm.chosenTeam ?? undefined}
          onChange={vm.selectTeam}
          multi={false}
          testId="compact-team-selector"
          renderItem={(itemProps) => {
            const display = getDisplayPropsForTeam(
              vm.workspaceMember ?? Member.none,
              itemProps.item,
              nullSelectionPlaceholder,
            )
            return (
              <SingleItemSelectedDefault
                visual={
                  display.avatar && (
                    <Avatar
                      size={24}
                      subject={{
                        displayName: display.name,
                        avatar: display.avatar,
                      }}
                    />
                  )
                }
                {...itemProps}
              />
            )
          }}
          renderSearchItem={({
            item,
            itemToString: _itemToString,
            ...itemProps
          }) => {
            const display = getDisplayPropsForTeam(
              vm.workspaceMember ?? Member.none,
              item,
              nullSelectionPlaceholder,
            )
            return (
              <SearchResultItem
                {...itemProps}
                visual={
                  display.avatar && (
                    <Avatar
                      size={24}
                      subject={{
                        avatar: display.avatar,
                        displayName: display.name,
                      }}
                    />
                  )
                }
              >
                {display.name}
              </SearchResultItem>
            )
          }}
        />
      </FormGroup>
    )
  },
)

/**
 * Gets display props for a team.
 */
function getDisplayPropsForTeam(
  member: Member,
  team: TeamSelection,
  nullSelectionPlaceholder: string,
) {
  if (team === TeamSelection.myself) {
    return {
      name: nullSelectionPlaceholder,
      avatar:
        nullSelectionPlaceholder === myselfDefaultPlaceholder
          ? member.avatar
          : null,
    }
  }

  return {
    name: team.name,
    avatar: team.avatar,
  }
}
